<template>
  <div ref="mapNode"
       class="google-map">
  </div>
</template>

<script>
import { Loader } from "@googlemaps/js-api-loader";

export default {
  props: {
    schools: {
      type: Array,
      default: null,
    },
  },

  data () {
    return {
      map: null,
      mapMarkers: [],

      mapOptions: {
        center: {
          lat: 0,
          lng: 0,
        },
        zoom: 8,
      },
    };
  },

  computed: {
    /**
     * Categories the provided schools into plottable and unplottable
     * Plottable schools contain latitude and longitude
     */
    categorisedSchools () {
      return ( this.schools?.length > 0 ? this.schools : [] )
        .reduce( ( categories, school ) => {
          if ( this.isValidCoordinate( school.longitude )
            && this.isValidCoordinate( school.latitude ) ) {
            categories.plottable.push( school );
          } else {
            categories.unplottable.push( school );
          }
          return categories;
        }, { plottable: [], unplottable: [] } );
    },
  },

  watch: {
    /**
     * Schools object has changed so update the pins
     */
    schools: {
      handler ( updated, old ) {
        const hasChanges = !updated.every( u => !!old.find( o => o.schoolName === u.schoolName ) );
        const hasPreviousValue = !!old && Array.isArray( old );
        const hasModifiedQuanitiy = !hasChanges && updated.length !== old.length;

        if ( hasChanges || hasModifiedQuanitiy || !hasPreviousValue ) {
          this.mapAndPlotSchools();
        }
      },
    },
  },

  /**
   * Set up the map when the component is first mounted
   */
  mounted () {
    // Create a loader for Google Maps
    const loader = new Loader( {
      apiKey: "AIzaSyCdrlZsA3RpcfWyxyUUATNi8PBCjut_3Nc",
      version: "weekly",
    } );

    // Use it to load the required map scripts, if scripts are already loaded this
    // will do nothing
    loader.load()
      .then( () => {
        // eslint-disable-next-line no-undef
        this.map = new google.maps.Map( this.$refs.mapNode, this.mapOptions );
      } )
      .catch( () => {
        this.map = null;
      } )
      .finally( () => {
        // Convert the schools to plotable pins and plot them
        this.mapAndPlotSchools();
      } );
  },

  methods: {
    /**
     * Determines if a value is a valid coordinate
     * @param {Number} coordinate
     */
    isValidCoordinate ( coordinate ) {
      return coordinate === 0 || coordinate > 0 || coordinate < 0;
    },
    /**
     * Plot schools on the mapa
     */
    mapAndPlotSchools () {
      // Clear existing schools from the map first
      this.clearSchools();

      // Plot on next tick to make sure all computed properties have updated
      this.$nextTick( () => {
        let plottableMappedSchools = [];
        let unplottableMappedSchools = [];

        // eslint-disable-next-line no-undef
        const bounds = new google.maps.LatLngBounds();

        if ( this.categorisedSchools.plottable?.length && this.map ) {
          plottableMappedSchools = this.categorisedSchools.plottable.map( school => {
            // eslint-disable-next-line no-undef
            const marker = new google.maps.Marker( {
              position: {
                lat: school.latitude,
                lng: school.longitude,
              },
              icon: this.createPinIcon( school.partnerSchoolType?.color ?? "red" ),
              title: school.schoolName,
            } );

            // Make sure the map bounds include this marker
            bounds.extend( marker.position );

            // Add marker to map
            marker.setMap( this.map );

            // Add a click event to the marker so that we can inform the parent when a
            // school is clicked
            marker.addListener( "click", () => {
              this.$emit( "marker-click", school );
            } );

            // Need to store the marker so that it can be removed from the map
            // at a later time
            this.mapMarkers.push( marker );

            const { map } = this;

            return {
              ...school,
              panToMarker () {
                map.setZoom( 17 );
                map.panTo( marker.position );
              },
            };
          } );
        }

        if ( this.categorisedSchools.unplottable?.length && this.map ) {
          unplottableMappedSchools = this.categorisedSchools.unplottable
            .map( u => ( {
              ...u,
              panToMarker: () => {
                console.warn( "No coordinates provided for school." );
                this.map.fitBounds( bounds );

                // pan out if only 1 plottable marker as it is not the selected school
                if ( this.categorisedSchools.plottable.length <= 1 ) {
                  this.map.setZoom( 10 );
                }
              },
            } ) );

          console.warn( "These schools were unplottable. Please check the coorditnates are defined for both longitude and latitude:\n\n", unplottableMappedSchools );
        }

        // only fit bounds if we have plotted schools
        if ( plottableMappedSchools.length > 0 ) {
          // Now fit the map bounds around the markers
          this.map.fitBounds( bounds );
        }

        // Emit a list of schools with a callback to the marker
        this.$emit( "mapped-schools", [...plottableMappedSchools, ...unplottableMappedSchools] );
      } );
    },

    /**
     * Clear all schools from the map
     */
    clearSchools () {
      if ( this.mapMarkers?.length && this.map ) {
        this.mapMarkers.forEach( marker => {
          marker.setMap( null );
        } );

        this.mapMarkers = [];
      }
    },

    /**
     * Create a custom map pin that is the correct colour
     *
     * @param {string} color Colour code (any hex HTML valid colour code)
     */
    createPinIcon ( color ) {
      // Pin  is 15.36 by 20.48. So the anchor needs to be 50% x 100% y
      // eslint-disable-next-line no-undef
      const anchorPoint = new google.maps.Point( 7.68, 20.48 );

      return {
        path:
      "M 6.8907 20.0668 C 1.0788 11.6412 0 10.7765 0 7.68 C 0 3.4384 3.4384 0 7.68 0 s 7.68 3.4384 7.68 7.68 c 0 3.0965 -1.0788 3.9612 -6.8907 12.3868 c -0.3814 0.551 -1.1972 0.5509 -1.5786 0 Z M 7.68 10.88 c 1.7673 0 3.2 -1.4327 3.2 -3.2 s -1.4327 -3.2 -3.2 -3.2 s -3.2 1.4327 -3.2 3.2 s 1.4327 3.2 3.2 3.2 Z",
        fillColor: `#${color}`,
        fillOpacity: 1,
        strokeWeight: 0,
        rotation: 0,
        scale: 2,
        anchor: anchorPoint,
      };
    },
  },
};
</script>
